import { Button } from "@ebsco-ui/ebsco-ui";
import { FORM_ERROR } from "final-form";
import createDecorator from "final-form-focus";
import queryString from "query-string";
import React from "react";
import { Form } from "react-final-form";
import { FormattedMessage, injectIntl } from "react-intl";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { compose } from "redux";
import sharedMessages from "../../../../shared-messages";
import {
  createPersonalUserFailure,
  createPersonalUserRequest,
  createPersonalUserSuccess,
  getIsSocialAuthEnabled
} from "../../../store/create";
import { getGdprPromptId, getGdprPromptLocale } from "../../../store/gdpr";
import { getGoogleSignupUrl } from "../../../store/navigation";
import mapApiErrorsToFormErrors from "../../../utils/map-api-errors-to-field-errors";
import submitErrorFieldsHaveChanged from "../../../utils/submit-error-fields-have-changed";
import CoppaStrategy from "../../coppa-strategy/coppa-strategy";
import { useEdgeApiContext } from "../../edge-api";
import FormErrorNotification from "../../form-error-notification";
import {
  CoppaUpdateUserFormFields,
  NonCoppaUpdateUserFormFields
} from "../create-user-form-types";
import messages from "./create-user-form-messages";
import DividerMessage from "./divider-message";
import SocialAuthLink from "./social-auth-link";
import submitMessages from "./submit-error-message-descriptors";

import "./create-user-form.scss";

const focusOnErrors = createDecorator();
const block = "create-user-form";

const CreateUserForm = ({
  intl: { formatMessage },
  location,
  createPersonalUsrRequest,
  createPersonalUsrSuccess,
  createPersonalUsrFailure,
  googleSignupUrl,
  isSocialAuthEnabled,
  promptId,
  locale
}) => {
  const personalUserApi = useEdgeApiContext();

  const onSubmit = async data => {
    const { custId, groupId, profileId, authToken } = queryString.parse(
      location.search
    );
    try {
      createPersonalUsrRequest();
      const body = {
        ...data,
        customerId: custId,
        groupId,
        profileId,
        promptId,
        locale
      };
      await personalUserApi.postPersonalUser(body, authToken);
      createPersonalUsrSuccess();
    } catch (error) {
      createPersonalUsrFailure(error);
      let formErrors = {
        [FORM_ERROR]: {
          headerMessage: formatMessage(sharedMessages.somethingWentWrong),
          descriptionMessage: formatMessage(
            sharedMessages.unableToCreateAccount
          )
        }
      };
      if (error.response) {
        const {
          response: { status, data }
        } = error;
        switch (status) {
          case 400:
            const mappedErrors = mapApiErrorsToFormErrors(data.details, key => {
              return submitMessages[key]
                ? formatMessage(submitMessages[key])
                : undefined;
            });
            if (Object.keys(mappedErrors).length) {
              formErrors = mappedErrors;
            }
            break;
          case 401:
            formErrors = {
              [FORM_ERROR]: {
                headerMessage: formatMessage(messages.accountNotCreated),
                descriptionMessage: formatMessage(messages.unauthorized)
              }
            };
            break;
          default:
            return formErrors;
        }
      }
      return formErrors;
    }
  };

  return (
    <div className={block}>
      {isSocialAuthEnabled && googleSignupUrl && (
        <>
          <SocialAuthLink
            text={formatMessage(messages.continueWithGoogle)}
            href={googleSignupUrl}
            className={`${block}__social-link`}
          />
          <DividerMessage
            message={formatMessage(messages.orContinueWithEmail)}
          />
        </>
      )}
      <Form onSubmit={onSubmit} decorators={[focusOnErrors]}>
        {({
          handleSubmit,
          submitting,
          submitFailed,
          submitErrors,
          submitError,
          dirtyFieldsSinceLastSubmit,
          dirty
        }) => {
          const disableSave =
            submitting ||
            !(submitFailed
              ? submitErrorFieldsHaveChanged(
                  submitErrors,
                  dirtyFieldsSinceLastSubmit
                )
              : dirty);
          return (
            <form onSubmit={handleSubmit}>
              {submitError && (
                <FormErrorNotification
                  className={`${block}__error`}
                  {...submitError}
                />
              )}
              <CoppaStrategy
                coppaComponent={CoppaUpdateUserFormFields}
                nonCoppaComponent={NonCoppaUpdateUserFormFields}
              />

              <Button
                className="create-button"
                type="submit"
                disabled={disableSave}
                styleType="solid"
              >
                <FormattedMessage {...messages.createAccount} />
              </Button>
            </form>
          );
        }}
      </Form>
    </div>
  );
};

const mapStateToProps = state => ({
  promptId: getGdprPromptId(state),
  locale: getGdprPromptLocale(state),
  isSocialAuthEnabled: getIsSocialAuthEnabled(state),
  googleSignupUrl: getGoogleSignupUrl(state)
});

const mapDispatchToProps = {
  createPersonalUsrRequest: createPersonalUserRequest,
  createPersonalUsrSuccess: createPersonalUserSuccess,
  createPersonalUsrFailure: createPersonalUserFailure
};

export { CreateUserForm };
export default compose(
  withRouter,
  injectIntl,
  connect(mapStateToProps, mapDispatchToProps)
)(CreateUserForm);
